import { SaveOutlined } from '@ant-design/icons'
import { Alert, Button, Card, Form, Input, Skeleton, Typography, message } from 'antd'
import { useCallback, useEffect, useState } from 'react'
import { getSiteSettings, updateSiteSettings } from '../../api'
import type { SaasApiError } from '../../api'
import type { SiteSettingsOut } from '../../types'

const { Title, Paragraph } = Typography

/**
 * "全站设置" (Site Settings) — spec §17/§20: Legacy's own admin has no
 * "Theme"/preset-switcher concept exposed to a normal museum
 * administrator (that is a Vistoera SaaS-platform internal, not a
 * LiangCollection user-facing capability) so the previous Theme summary
 * card has been removed from this screen entirely — real Site Settings
 * (title/description/contact/footer/language) + the tenant-configurable
 * Item vocabulary (spec §7 — "Item -> 条目/作品/商品") are what remain.
 * Every field here is backed by `app.saas.models.site_settings.
 * SiteSettings` / `Site.vocabulary_json`, never a frontend-only mock.
 *
 * GAP this round (see docs/migration/LIANGCOLLECTION_UI_TOKEN_MAP.md):
 * Legacy's SiteSettings also has structured footer links, multi-size
 * favicon uploads, and ICP/公安备案 fields — not yet added here.
 */
export default function DesignTab({ workspaceId, siteId }: { workspaceId: string; siteId: string }) {
  const [settings, setSettings] = useState<SiteSettingsOut | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [form] = Form.useForm()

  const load = useCallback(() => {
    setError(null)
    getSiteSettings(workspaceId, siteId)
      .then((r) => {
        setSettings(r.data)
        form.setFieldsValue({
          title: r.data.title,
          description: r.data.description,
          contact_email: r.data.contact_email ?? '',
          footer_text: r.data.footer_text,
          item_singular: r.data.vocabulary?.item?.singular ?? '条目',
          item_plural: r.data.vocabulary?.item?.plural ?? '条目',
        })
      })
      .catch((err) => setError((err as SaasApiError).message))
  }, [workspaceId, siteId, form])

  useEffect(load, [load])

  const onSave = async () => {
    const values = await form.validateFields()
    setSaving(true)
    try {
      const { data } = await updateSiteSettings(workspaceId, siteId, {
        title: values.title,
        description: values.description,
        contact_email: values.contact_email || null,
        footer_text: values.footer_text,
        vocabulary: { item: { singular: values.item_singular, plural: values.item_plural } },
      })
      setSettings(data)
      message.success('全站设置已保存')
    } catch (err) {
      message.error((err as SaasApiError).message)
    } finally {
      setSaving(false)
    }
  }

  if (error) return <Alert type="error" message={error} showIcon style={{ marginTop: 16 }} />
  if (settings === null) return <Skeleton active paragraph={{ rows: 8 }} style={{ marginTop: 16 }} />

  return (
    <div style={{ marginTop: 16, maxWidth: 560 }}>
      <Title level={5}>全站设置</Title>
      <Card>
        <Form form={form} layout="vertical">
          <Form.Item name="title" label="网站名称" rules={[{ required: true, message: '请输入网站名称' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="description" label="网站描述">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item name="contact_email" label="联系邮箱">
            <Input placeholder="hello@example.com" />
          </Form.Item>
          <Form.Item name="footer_text" label="页脚文字">
            <Input placeholder="© 2026 LiangCollection" />
          </Form.Item>
        </Form>
      </Card>

      <Title level={5} style={{ marginTop: 24 }}>
        条目称谓
      </Title>
      <Paragraph type="secondary">
        为本站的「条目」核心概念自定义显示名称——底层数据不变，仅显示名称不同（例如博物馆用"条目"，作品集用"作品"，商店用"商品"）。
      </Paragraph>
      <Card>
        <Form form={form} layout="vertical">
          <Form.Item name="item_singular" label="单数名称" rules={[{ required: true, message: '请输入单数名称' }]}>
            <Input placeholder="条目" />
          </Form.Item>
          <Form.Item name="item_plural" label="复数名称" rules={[{ required: true, message: '请输入复数名称' }]}>
            <Input placeholder="条目" />
          </Form.Item>
        </Form>
      </Card>

      <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={onSave} style={{ marginTop: 16 }}>
        保存设置
      </Button>
    </div>
  )
}
